import { RequireAccess } from '@/components'
import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { roleEnum } from '@jobilla/nexus-sdk'
import {
	organisationPrivacyShowQueryKey,
	OrganisationPrivacyShowQueryResponse,
	useCandidateDestroyByEmail,
	useCandidateFindByEmail,
	useOrganisationPrivacyShow,
	useOrganisationPrivacyUpdate,
	useOrganisationUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { InfoIcon } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	CandidateSearchResult,
	CompanyDataRetentionPolicy,
	CompanyDataRetentionPolicyViewModel,
	CompanyPrivacyPolicy,
	CompanyPrivacyPolicyViewModel,
	DataRemovalRequests,
	SettingsHeading,
	SettingsNav,
	SwitchField,
} from '../../../../../ui'

export const Route = createFileRoute('/_auth/company/$companyId/settings/privacy')({
	component: RouteComponent,
})

function RouteComponent() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const postHog = usePostHog()

	const [candidateSearchQuery, setCandidateSearchQuery] = useState('')
	const [candidateSearchResult, setCandidateSearchResult] = useState<CandidateSearchResult | null>(null)

	const { role } = useAuth()
	const canManagePrivacy = role === roleEnum.admin || role === roleEnum.jobillan

	const privacyQuery = useOrganisationPrivacyShow(jobillaOrgId, {
		enabled: !!jobillaOrgId && canManagePrivacy,
	})

	const queryClient = useQueryClient()

	const candidateFindByEmailMutation = useCandidateFindByEmail({
		onSuccess: (response) => {
			const candidate = response
			setCandidateSearchResult({
				name: candidate.name ?? null,
				email: candidate.email ?? null,
				applicationsCount: candidate.applicationCount ?? 0,
			})
		},
		onError: () => {
			setCandidateSearchResult(null)
		},
	})

	const candidateDeleteMutation = useCandidateDestroyByEmail({
		onSuccess: () => {
			setCandidateSearchResult(null)
			setCandidateSearchQuery('')
			postHog.capture('organisation_settings:candidate_data_deleted', {
				company_id: jobillaOrgId,
			})
		},
	})

	const organizationMutation = useOrganisationUpdate({
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: organisationPrivacyShowQueryKey(jobillaOrgId),
			})
		},
	})

	const dataRetentionPolicyMutation = useOrganisationPrivacyUpdate({
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: organisationPrivacyShowQueryKey(jobillaOrgId),
			})
		},
		onMutate: (variables) => {
			void queryClient.cancelQueries({
				queryKey: organisationPrivacyShowQueryKey(jobillaOrgId),
			})

			const previousData: OrganisationPrivacyShowQueryResponse | undefined = queryClient.getQueryData(
				organisationPrivacyShowQueryKey(jobillaOrgId),
			)

			if (!previousData) {
				return
			}

			queryClient.setQueryData(organisationPrivacyShowQueryKey(jobillaOrgId), {
				...previousData,
				candidate_retention_months: Number(variables.data?.candidateRetentionMonths),
			})

			return { previousData }
		},
		onError: (_err, _data, context) => {
			const ctx = context as { previousData?: OrganisationPrivacyShowQueryResponse } | undefined
			if (ctx?.previousData) {
				queryClient.setQueryData(organisationPrivacyShowQueryKey(jobillaOrgId), ctx.previousData)
			}
		},
	})

	const dpoData = privacyQuery.data?.dataProtectionOfficer
	const orgInfoData = privacyQuery.data?.organisationInformation

	const companyPrivacyPolicyViewModel: CompanyPrivacyPolicyViewModel = {
		view: {
			privacyPolicyLink: privacyQuery.data?.privacyPolicyUrl || '',
			dpoName: dpoData?.name || '',
			dpoPhone: dpoData?.phoneNumber || '',
			dpoEmail: dpoData?.emailAddress || '',
			legalEntityName: orgInfoData?.legalEntityName || '',
			orgStreetAddress: orgInfoData?.streetAddress || '',
			orgPostalCode: orgInfoData?.postalCode || '',
			orgCity: orgInfoData?.city || '',
			orgCountry: orgInfoData?.country || '',
			orgPhone: orgInfoData?.phoneNumber || '',
			orgEmail: orgInfoData?.emailAddress || '',
			companyId: jobillaOrgId || '',
		},
		loading: {
			privacyPolicyLink: privacyQuery.isPending,
			privacyPolicySaving: organizationMutation.isPending,
		},
		errors: {
			privacyPolicyLink: organizationMutation.error?.message ?? '',
			dpoName: '',
			dpoPhone: '',
			dpoEmail: '',
			legalEntityName: '',
			orgStreetAddress: '',
			orgPostalCode: '',
			orgCity: '',
			orgCountry: '',
			orgPhone: '',
			orgEmail: '',
		},
		events: {
			onSavePrivacySettings: (data) => {
				if (!jobillaOrgId) {
					return
				}

				organizationMutation.mutate({
					organisation: jobillaOrgId,
					data: {
						privacyPolicyUrl: data.privacyPolicyLink || null,
						dataProtectionOfficer: {
							name: data.dpoName || null,
							phoneNumber: data.dpoPhone || null,
							emailAddress: data.dpoEmail || null,
						},
						organisationInformation: {
							legalEntityName: data.legalEntityName || null,
							streetAddress: data.orgStreetAddress || null,
							postalCode: data.orgPostalCode || null,
							city: data.orgCity || null,
							country: data.orgCountry || null,
							phoneNumber: data.orgPhone || null,
							emailAddress: data.orgEmail || null,
						},
					},
				})
			},
		},
	}

	const companyDataRetentionPolicyViewModel: CompanyDataRetentionPolicyViewModel = {
		view: {
			policyRetentionPeriodInMonths: privacyQuery.data?.candidateRetentionMonths || 12,
			policyRetentionPeriodOptionsInMonths: [
				3,
				6,
				9,
				12,
				15,
				18,
				24,
				48,
			],
			candidateSearchQuery,
			candidateSearchResult,
			isSearching: candidateFindByEmailMutation.isPending,
			isDeleting: candidateDeleteMutation.isPending,
		},
		errors: {
			policyRetentionPeriodError: dataRetentionPolicyMutation.error?.message
				? 'Failed to save data retention. Please try again later.'
				: undefined,
			candidateSearchError: candidateFindByEmailMutation.error
				? 'Could not find candidate with the provided email address.'
				: undefined,
			candidateDeleteError: candidateDeleteMutation.error
				? 'Could not delete candidate data. Please try again later.'
				: undefined,
		},
		events: {
			onPolicyRetentionPeriodChange: (months) => {
				postHog.capture('organisation_settings:data_retention_policy_changed', {
					company_id: jobillaOrgId,
					new_retention_period_in_months: months,
				})
				const organisationData = privacyQuery.data
				if (!organisationData) {
					return
				}
				dataRetentionPolicyMutation.mutate({
					organisation: jobillaOrgId,
					data: {
						candidateRetentionMonths: months,
					},
				})
			},
			onCandidateSearchQueryChange: (email) => {
				setCandidateSearchQuery(email)
				// Clear previous result and error when query changes
				if (candidateSearchResult) {
					setCandidateSearchResult(null)
				}
				if (candidateFindByEmailMutation.error) {
					candidateFindByEmailMutation.reset()
				}
			},
			onCandidateSearch: () => {
				const trimmedQuery = candidateSearchQuery.trim()
				if (trimmedQuery && jobillaOrgId) {
					candidateFindByEmailMutation.mutate({
						data: { email: trimmedQuery, organisationId: jobillaOrgId },
					})
				}
			},
			onCandidateDelete: () => {
				candidateDeleteMutation.mutate({
					params: { email: candidateSearchQuery.trim(), organisationId: jobillaOrgId },
				})
			},
		},
	}

	return (
		<SettingsNav active="privacy">
			<SettingsHeading>{t('settings.companyPrivacyPolicy.title')}</SettingsHeading>
			<RequireAccess
				role={[roleEnum.admin, roleEnum.jobillan]}
				fallback={
					<p className="text-red-600">
						{t('settings.companyPrivacyPolicy.noPermission')}
					</p>
				}
			>
				<div className="space-y-16 max-w-[800px]">
					<CompanyPrivacyPolicy viewModel={companyPrivacyPolicyViewModel} />
					<CompanyDataRetentionPolicy viewModel={companyDataRetentionPolicyViewModel} />
					<div>
						<SwitchField
							checked={privacyQuery.data?.autoApproveDataRemovalRequests ?? true}
							onChange={(checked) => {
								if (!jobillaOrgId) {
									return
								}
								dataRetentionPolicyMutation.mutate({
									organisation: jobillaOrgId,
									data: {
										candidateRetentionMonths: privacyQuery.data?.candidateRetentionMonths ?? null,
										autoApproveDataRemovalRequests: checked,
									},
								})
							}}
							label={t('settings.dataRemovalRequests.autoApproveLabel')}
							description={t('settings.dataRemovalRequests.autoApproveDescription')}
							isLoading={privacyQuery.isPending}
							disabled={dataRetentionPolicyMutation.isPending}
						/>
						<div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4">
							<InfoIcon className="size-5 text-blue-600 shrink-0 mt-0.5" />
							<p className="text-sm text-blue-800">
								{t('settings.dataRemovalRequests.autoApproveInfo')}
							</p>
						</div>
					</div>
					<DataRemovalRequests />
				</div>
			</RequireAccess>
		</SettingsNav>
	)
}
